import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Z_INDEX, zStyle } from '../../lib/zLayers';
import { CalendarIcon, XIcon, ClockIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon } from './Icons';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function getDaysInMonth(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const days: Date[] = [];
  for (let d = 1; d <= last.getDate(); d++) {
    days.push(new Date(year, month, d));
  }
  return days;
}

function getCalendarGrid(year: number, month: number): (Date | null)[] {
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay();
  const days = getDaysInMonth(year, month);
  const grid: (Date | null)[] = [];
  for (let i = 0; i < startWeekday; i++) grid.push(null);
  days.forEach((d) => grid.push(d));
  while (grid.length < 42) grid.push(null);
  return grid;
}

function isSameDay(a: Date | null, b: Date | null): boolean {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isToday(d: Date | null): boolean {
  if (!d) return false;
  const t = new Date();
  return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
}

interface DateTimeInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange' | 'size'> {
  value?: string;
  onChange?: (event: { target: { name?: string; value: string; type: string } }) => void;
  placeholder?: string;
  showClearButton?: boolean;
  showQuickFill?: boolean;
  minDate?: string;
  maxDate?: string;
  dateTimeFormat?: string;
  error?: string;
  helperText?: string;
  size?: 'sm' | 'default';
}

const DateTimeInput: React.FC<DateTimeInputProps> = ({
  value,
  onChange,
  name,
  placeholder = 'Select date and time',
  required = false,
  readOnly = false,
  disabled = false,
  className,
  minDate,
  maxDate,
  showClearButton = true,
  showQuickFill = true,
  dateTimeFormat = 'yyyy-MM-ddTHH:mm',
  error,
  helperText,
  size = 'default',
  ...props
}) => {
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [keyboardInputValue, setKeyboardInputValue] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownRect, setDropdownRect] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    placement: 'top' | 'bottom';
  } | null>(null);

  const valueDate = value ? new Date(value) : null;
  const isValidValue = valueDate && !isNaN(valueDate.getTime());

  const [viewMonth, setViewMonth] = useState(() => {
    if (isValidValue) return new Date(valueDate!.getFullYear(), valueDate!.getMonth(), 1);
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState<Date | null>(isValidValue ? new Date(valueDate!.getFullYear(), valueDate!.getMonth(), valueDate!.getDate()) : null);
  const [hours, setHours] = useState(isValidValue ? valueDate!.getHours() : 0);
  const [minutes, setMinutes] = useState(isValidValue ? valueDate!.getMinutes() : 0);
  const [timeSelectMode, setTimeSelectMode] = useState<'hour' | 'minute'>('hour');

  const minDateObj = minDate ? new Date(minDate) : null;
  const maxDateObj = maxDate ? new Date(maxDate) : null;

  const formatDatePart = (dateTimeString: string): string => {
    if (!dateTimeString) return '';
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return '';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return '';
    }
  };

  const formatTimePart = (dateTimeString: string): string => {
    if (!dateTimeString) return '';
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return '';
      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    } catch {
      return '';
    }
  };

  const formatTimePart12h = (dateTimeString: string): string => {
    if (!dateTimeString) return '';
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return '';
      const h = date.getHours();
      const m = date.getMinutes();
      const hour12 = h % 12 || 12;
      const period = h >= 12 ? 'PM' : 'AM';
      return `${String(hour12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
    } catch {
      return '';
    }
  };

  const formatDateTimeForDisplay = (dateTimeString: string): string => {
    const d = formatDatePart(dateTimeString);
    const t = formatTimePart(dateTimeString);
    return d && t ? `${d} ${t}` : '';
  };

  const emitValue = (date: Date | null, h: number, m: number) => {
    if (!date) {
      onChange?.({ target: { name, value: '', type: 'datetime-local' } });
      return;
    }
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, m, 0, 0);
    onChange?.({ target: { name, value: d.toISOString(), type: 'datetime-local' } });
  };

  const parseTypedDateTime = (raw: string): Date | null => {
    const s = raw.trim();
    if (!s) return null;
    // Parse DD/MM/YYYY or DD-MM-YYYY first so we never confuse day with month (JS Date() uses MM/DD/YYYY)
    const dmyMatch = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})(?:\s+(\d{1,2})?:?(\d{1,2})?)?/);
    if (dmyMatch) {
      const [, day, month, year, h, m] = dmyMatch;
      const d = parseInt(day, 10);
      const mo = parseInt(month, 10) - 1;
      const y = year.length <= 2 ? 2000 + parseInt(year, 10) : parseInt(year, 10);
      const parsed = new Date(y, mo, d, parseInt(h || '0', 10), parseInt(m || '0', 10));
      if (!isNaN(parsed.getTime()) && parsed.getDate() === d && parsed.getMonth() === mo) return parsed;
    }
    // Fallback for ISO or other formats
    const d = new Date(s);
    if (!isNaN(d.getTime())) return d;
    return null;
  };

  const commitKeyboardInput = () => {
    const parsed = parseTypedDateTime(keyboardInputValue);
    if (parsed) {
      setViewMonth(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
      setSelectedDate(new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()));
      setHours(parsed.getHours());
      setMinutes(parsed.getMinutes());
      emitValue(
        new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()),
        parsed.getHours(),
        parsed.getMinutes()
      );
    }
    setKeyboardInputValue('');
  };

  useLayoutEffect(() => {
    if (!isOpen || !containerRef.current) {
      setDropdownRect(null);
      return;
    }
    const updateRect = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
      const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
      const gap = 6;
      const horizontalPad = 8;
      // Header + calendar + clock + quick-fill footer
      const preferredHeight = 480;
      const width = Math.min(440, Math.max(rect.width, 320), vw - horizontalPad * 2);
      let left = rect.left;
      if (left + width > vw - horizontalPad) {
        left = Math.max(horizontalPad, vw - horizontalPad - width);
      }
      if (left < horizontalPad) left = horizontalPad;

      const spaceBelow = Math.max(0, vh - rect.bottom - gap);
      const spaceAbove = Math.max(0, rect.top - gap);
      const openUp = spaceBelow < preferredHeight && spaceAbove > spaceBelow;

      if (openUp) {
        setDropdownRect({
          bottom: vh - rect.top + gap,
          left,
          width,
          placement: 'top',
        });
      } else {
        setDropdownRect({
          top: rect.bottom + gap,
          left,
          width,
          placement: 'bottom',
        });
      }
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = containerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inTrigger && !inDropdown) {
        setIsOpen(false);
        setKeyboardInputValue('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, []);

  useEffect(() => {
    if (isOpen) setTimeSelectMode('hour');
  }, [isOpen]);

  useEffect(() => {
    if (value) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1));
        setSelectedDate(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
        setHours(d.getHours());
        setMinutes(d.getMinutes());
      }
    } else {
      setSelectedDate(null);
      setHours(0);
      setMinutes(0);
    }
  }, [value]);

  const handleSelectDay = (d: Date | null) => {
    if (!d) return;
    if (minDateObj && d < minDateObj) return;
    if (maxDateObj && d > maxDateObj) return;
    setSelectedDate(d);
    emitValue(d, hours, minutes);
  };

  const handleTimeChange = (newHours: number, newMinutes: number) => {
    const h = Math.max(0, Math.min(23, newHours));
    const m = Math.max(0, Math.min(59, newMinutes));
    setHours(h);
    setMinutes(m);
    const base = selectedDate || new Date();
    const day = selectedDate
      ? selectedDate
      : new Date(base.getFullYear(), base.getMonth(), base.getDate());
    if (!selectedDate) setSelectedDate(day);
    emitValue(day, h, m);
  };

  const hour12 = hours % 12 || 12;
  const isPM = hours >= 12;
  const rollHour = (delta: number) => () => {
    let h = hours + delta;
    if (h < 0) h = 23;
    if (h > 23) h = 0;
    handleTimeChange(h, minutes);
  };
  const rollMinute = (delta: number) => () => {
    let m = minutes + delta;
    if (m < 0) m = 59;
    if (m > 59) m = 0;
    handleTimeChange(hours, m);
  };
  const setPM = () => {
    if (!isPM) handleTimeChange(hours + 12, minutes);
  };
  const setAM = () => {
    if (isPM) handleTimeChange(hours - 12, minutes);
  };
  const setHourFromClock = (n: number) => {
    const h = isPM ? (n === 12 ? 12 : n + 12) : (n === 12 ? 0 : n);
    handleTimeChange(h, minutes);
    setTimeSelectMode('minute');
  };
  const setMinuteFromClock = (m: number) => {
    handleTimeChange(hours, m);
  };
  const minuteOptions = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setSelectedDate(null);
    setHours(0);
    setMinutes(0);
    setKeyboardInputValue('');
    onChange?.({ target: { name, value: '', type: 'datetime-local' } });
  };

  const applyQuickFill = (date: Date) => {
    setViewMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    setSelectedDate(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
    setHours(date.getHours());
    setMinutes(date.getMinutes());
    emitValue(
      new Date(date.getFullYear(), date.getMonth(), date.getDate()),
      date.getHours(),
      date.getMinutes()
    );
  };

  const quickFillFromNow = (minutesOffset: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || readOnly) return;
    const d = new Date();
    d.setMinutes(d.getMinutes() + minutesOffset);
    applyQuickFill(d);
  };

  const isDayDisabled = (d: Date | null) => {
    if (!d) return true;
    if (minDateObj && d < minDateObj) return true;
    if (maxDateObj && d > maxDateObj) return true;
    return false;
  };

  const displayValue = value && formatDateTimeForDisplay(value);
  const hasValue = !!displayValue;
  const grid = getCalendarGrid(viewMonth.getFullYear(), viewMonth.getMonth());

  const currentDateTimeDisplay =
    value
      ? formatDateTimeForDisplay(value)
      : selectedDate
        ? `${String(selectedDate.getDate()).padStart(2, '0')}/${String(selectedDate.getMonth() + 1).padStart(2, '0')}/${selectedDate.getFullYear()} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
        : '';

  return (
    <div ref={containerRef} className="relative">
      <input type="hidden" name={name} value={value || ''} readOnly {...props} />

      <div className="relative">
        <div
          className={cn(
            'flex items-center w-full rounded-lg transition-all duration-200 border',
            size === 'sm' ? 'min-h-8 px-2 py-1 text-xs' : 'min-h-10 px-2.5 py-2 text-sm',
            colors.surface,
            colors.border,
            isOpen && 'ring-2 shadow-sm',
            isOpen && colors.primaryBorder,
            !isOpen && !error && colors.interactiveHover,
            error && 'border-red-500',
            disabled && 'opacity-50 cursor-not-allowed',
            disabled && colors.surfaceHover,
            readOnly && 'cursor-default',
            readOnly && colors.surfaceHover,
            !disabled && !readOnly && 'cursor-pointer',
            className
          )}
          onClick={() => !disabled && !readOnly && setIsOpen(!isOpen)}
        >
          <div className={cn('flex items-center gap-1 flex-1 min-w-0', !hasValue && 'opacity-70')}>
            <CalendarIcon
              className={cn(
                'flex-shrink-0',
                size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5',
                hasValue ? colors.primaryFg : colors.textMuted,
              )}
            />
            <div className="flex items-center gap-1 flex-1 min-w-0">
              {hasValue ? (
                <>
                  <span
                    className={cn(
                      'font-medium tabular-nums',
                      size === 'sm' ? 'text-xs' : 'text-sm',
                      colors.primaryFg,
                    )}
                  >
                    {formatDatePart(value!)}
                  </span>
                  {size !== 'sm' ? (
                    <span className={cn('text-xs font-medium px-1 py-0.5 rounded-md shrink-0', colors.info)}>
                      Date
                    </span>
                  ) : null}
                </>
              ) : (
                <span className={cn(size === 'sm' ? 'text-xs' : 'text-sm', colors.textMuted)}>Date</span>
              )}
            </div>
          </div>
          {hasValue && (
            <div
              className={cn(
                'w-px mx-0.5 rounded-full',
                size === 'sm' ? 'h-3.5' : 'h-5',
                colors.border,
              )}
            />
          )}
          <div className={cn('flex items-center gap-1 flex-1 min-w-0', !hasValue && 'opacity-70')}>
            <ClockIcon
              className={cn(
                'flex-shrink-0',
                size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5',
                hasValue ? colors.primaryFg : colors.textMuted,
              )}
            />
            <div className="flex items-center gap-1 flex-1 min-w-0">
              {hasValue ? (
                <>
                  <span
                    className={cn(
                      'font-medium tabular-nums',
                      size === 'sm' ? 'text-xs' : 'text-sm',
                      colors.primaryFg,
                    )}
                  >
                    {formatTimePart(value!)}
                  </span>
                  {size !== 'sm' ? (
                    <span className={cn('text-xs font-medium px-1 py-0.5 rounded-md shrink-0', colors.info)}>
                      Time
                    </span>
                  ) : null}
                </>
              ) : (
                <span className={cn(size === 'sm' ? 'text-xs' : 'text-sm', colors.textMuted)}>Time</span>
              )}
            </div>
          </div>
          {showClearButton && hasValue && !disabled && !readOnly && (
            <button
              type="button"
              onClick={handleClear}
              className={cn(
                'ml-0.5 rounded-md flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors',
                size === 'sm' ? 'p-1' : 'p-1.5',
                colors.textMuted,
                colors.interactiveHover,
                colors.primaryBorderAll
                  ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}`
                  : '',
              )}
              title="Clear"
              tabIndex={-1}
            >
              <XIcon className={size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
            </button>
          )}
        </div>
      </div>

      {isOpen && !readOnly && !disabled && dropdownRect &&
        createPortal(
          <div
            ref={dropdownRef}
            data-datetime-input-dropdown=""
            className={cn(
              'fixed rounded-xl shadow-lg border overflow-hidden',
              colors.surface,
              colors.border,
            )}
            style={{
              ...zStyle(Z_INDEX.portaledDropdown),
              left: dropdownRect.left,
              width: dropdownRect.width,
              ...(dropdownRect.placement === 'top'
                ? { bottom: dropdownRect.bottom }
                : { top: dropdownRect.top }),
            }}
          >
          {/* Header: DATE [date box] ... TIME [time box] */}
          <div className={cn('flex flex-row gap-2 px-2.5 py-2 items-center border-b', colors.border, colors.text, colors.surfaceHover)}>
            <span className={cn('text-xs font-semibold uppercase tracking-wider shrink-0', colors.textMuted)}>Date</span>
            <div
              className={cn(
                'flex-1 min-w-0 px-2 py-1 rounded border text-sm font-medium tabular-nums',
                colors.border,
                colors.surface,
                colors.text
              )}
            >
              {selectedDate
                ? `${String(selectedDate.getDate()).padStart(2, '0')}/${String(selectedDate.getMonth() + 1).padStart(2, '0')}/${selectedDate.getFullYear()}`
                : ''}
            </div>
            <span className={cn('text-xs font-semibold uppercase tracking-wider shrink-0', colors.textMuted)}>Time</span>
            <div
              className={cn(
                'w-16 px-2 py-1 rounded border text-sm font-medium text-center tabular-nums',
                colors.border,
                colors.surface,
                colors.text
              )}
            >
              {value ? formatTimePart(value) : ''}
            </div>
          </div>
          <div className="px-2.5 py-2">
          {/* Horizontal: Date (left) | Time (right) */}
          <div className="flex flex-row items-start gap-3.5">
            {/* Calendar - Date */}
            <div className="flex-shrink-0">
              <div className="flex items-center justify-between gap-1 mb-1">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
                  }}
                  className={cn('p-1 rounded border transition-colors', colors.border, colors.interactiveHover, colors.text)}
                  aria-label="Previous month"
                >
                  <ChevronLeftIcon className="w-3.5 h-3.5" />
                </button>
                <span className={cn('text-sm font-semibold flex-1 text-center tabular-nums', colors.text)}>
                  {MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
                  }}
                  className={cn('p-1 rounded border transition-colors', colors.border, colors.interactiveHover, colors.text)}
                  aria-label="Next month"
                >
                  <ChevronRightIcon className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-0.5 mb-1">
                {WEEKDAYS.map((wd) => (
                  <div key={wd} className={cn('text-center text-xs font-semibold py-0.5', colors.textMuted)}>
                    {wd}
                  </div>
                ))}
                {grid.map((cell, i) => {
                  const disabled = isDayDisabled(cell);
                  const selected = selectedDate && cell && isSameDay(cell, selectedDate);
                  const today = isToday(cell);
                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={disabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectDay(cell);
                      }}
                      className={cn(
                        'w-7 h-7 rounded text-sm font-medium transition-all duration-150',
                        !cell && 'invisible',
                        cell && disabled && 'opacity-40 cursor-not-allowed',
                        cell && !disabled && !selected && colors.interactiveHover,
                        selected && cn(colors.primary, colors.primaryText, 'shadow-sm hover:opacity-90'),
                        !selected && today && 'ring-2',
                        !selected && today && colors.primaryBorder,
                        !selected && today && colors.surfaceHover,
                        cell && !selected && !disabled && colors.text
                      )}
                    >
                      {cell ? cell.getDate() : ''}
                    </button>
                  );
                })}
              </div>
              <div className={cn('pt-1 mt-0.5 border-t', colors.border)}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleClear(e); }}
                  className={cn('text-xs font-medium px-1.5 py-0.5 rounded transition-colors', colors.primaryFg, colors.interactiveHover)}
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Time: analog clock (hour then minute selection) */}
            <div className={cn('flex flex-col flex-shrink-0 items-center justify-start min-h-0 w-[184px]', colors.text)}>
              {/* Hour / Minute mode toggle */}
              <div className="flex gap-1 mb-1">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setTimeSelectMode('hour'); }}
                  className={cn(
                    'px-2 py-1 text-xs font-semibold rounded transition-all duration-150',
                    timeSelectMode === 'hour'
                      ? cn(colors.primary, colors.primaryText, 'shadow-sm')
                      : cn(colors.surfaceHover, colors.textMuted, colors.interactiveHover)
                  )}
                >
                  Hour
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setTimeSelectMode('minute'); }}
                  className={cn(
                    'px-2 py-1 text-xs font-semibold rounded transition-all duration-150',
                    timeSelectMode === 'minute'
                      ? cn(colors.primary, colors.primaryText, 'shadow-sm')
                      : cn(colors.surfaceHover, colors.textMuted, colors.interactiveHover)
                  )}
                >
                  Minute
                </button>
              </div>
              {/* Analog clock face - 160px */}
              <div className={cn('relative w-[160px] h-[160px] rounded-full border-2 shadow-inner', colors.surfaceHover, colors.border)}>
                {timeSelectMode === 'hour' ? (
                  <>
                    <div
                      className={cn('absolute left-1/2 top-1/2 w-0.5 origin-bottom', colors.primary)}
                      style={{
                        height: '44px',
                        transform: `translate(-50%, -100%) rotate(${hour12 * 30}deg)`,
                      }}
                    />
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => {
                      const angle = ((n - 3) * 30 * Math.PI) / 180;
                      const r = 51;
                      const cx = 80;
                      const cy = 80;
                      const x = cx + r * Math.cos(angle);
                      const y = cy + r * Math.sin(angle);
                      const selected = hour12 === n;
                      return (
                        <button
                          key={n}
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setHourFromClock(n); }}
                          className={cn(
                            'absolute w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors',
                            selected ? cn(colors.primary, colors.primaryText) : cn(colors.interactiveHover, colors.text)
                          )}
                          style={{ left: x - 16, top: y - 16 }}
                          aria-label={`Hour ${n}`}
                        >
                          {n}
                        </button>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <div
                      className={cn('absolute left-1/2 top-1/2 w-0.5 origin-bottom', colors.primary)}
                      style={{
                        height: '44px',
                        transform: `translate(-50%, -100%) rotate(${minutes * 6}deg)`,
                      }}
                    />
                    {minuteOptions.map((m) => {
                      const i = m / 5;
                      const angle = (((i === 0 ? 12 : i) - 3) * 30 * Math.PI) / 180;
                      const r = 51;
                      const cx = 80;
                      const cy = 80;
                      const x = cx + r * Math.cos(angle);
                      const y = cy + r * Math.sin(angle);
                      const selected = minutes === m;
                      return (
                        <button
                          key={m}
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setMinuteFromClock(m); }}
                          className={cn(
                            'absolute w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors tabular-nums',
                            selected ? cn(colors.primary, colors.primaryText) : cn(colors.interactiveHover, colors.text)
                          )}
                          style={{ left: x - 16, top: y - 16 }}
                          aria-label={`Minute ${m}`}
                        >
                          {String(m).padStart(2, '0')}
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
              {/* AM / PM toggle - at bottom of analog clock */}
              <div className="flex gap-1 mt-1">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setAM(); }}
                  className={cn(
                    'px-3 py-1 text-xs font-semibold rounded transition-all duration-150',
                    !isPM
                      ? cn(colors.primary, colors.primaryText, 'shadow-sm')
                      : cn(colors.surfaceHover, colors.textMuted, colors.interactiveHover)
                  )}
                >
                  AM
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setPM(); }}
                  className={cn(
                    'px-3 py-1 text-xs font-semibold rounded transition-all duration-150',
                    isPM
                      ? cn(colors.primary, colors.primaryText, 'shadow-sm')
                      : cn(colors.surfaceHover, colors.textMuted, colors.interactiveHover)
                  )}
                >
                  PM
                </button>
              </div>
            </div>
          </div>

          {/* Type date/time + quick-fill */}
          <div
            className={cn(
              'mt-2 rounded-lg border px-2.5 py-2',
              colors.border,
              colors.surfaceHover
            )}
          >
            <div className={cn('flex items-center gap-1.5 mb-1.5', colors.text)}>
              <span className={cn('text-xs font-medium whitespace-nowrap', colors.textMuted)}>Or type</span>
              <input
                type="text"
                value={keyboardInputValue !== '' ? keyboardInputValue : currentDateTimeDisplay}
                onChange={(e) => setKeyboardInputValue(e.target.value)}
                onFocus={() => setKeyboardInputValue(currentDateTimeDisplay)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), commitKeyboardInput())}
                onBlur={commitKeyboardInput}
                placeholder="DD/MM/YYYY HH:MM"
                className={cn(
                  'flex-1 min-w-0 px-2 py-1.5 text-sm border rounded outline-none focus:ring-2 focus:ring-offset-0 transition-shadow',
            colors.border,
            colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '',
                  colors.surface,
                  colors.border,
                  colors.text
                )}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            {showQuickFill && (
              <div className="flex flex-wrap items-center gap-1">
                <span className={cn('text-xs font-medium', colors.textMuted)}>Quick</span>
                {[
                  { label: 'Now', min: 0 },
                  { label: '15', min: 15 },
                  { label: '30', min: 30 },
                  { label: '45', min: 45 },
                  { label: '60', min: 60 },
                ].map(({ label, min }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={quickFillFromNow(min)}
                    className={cn(
                      'px-2 py-1 text-xs font-medium rounded transition-colors',
                      min === 0
                        ? cn(colors.info, colors.interactiveHover)
                        : cn(colors.surfaceHover, colors.textSecondary, colors.interactiveHover)
                    )}
                    title={min === 0 ? (label === 'Today' ? "Today's date and time" : 'Current time') : `+${min} min from now`}
                  >
                    {min === 0 ? label : `+${label} min`}
                  </button>
                ))}
              </div>
            )}
          </div>
          </div>
        </div>,
          document.body,
        )}

      <div className="mt-1 flex flex-col gap-0.5">
        {error && <div className={cn('text-sm', colors.error)}>{error}</div>}
        {!error && helperText && <div className={cn('text-sm', colors.textMuted)}>{helperText}</div>}
      </div>
    </div>
  );
};

DateTimeInput.displayName = 'DateTimeInput';

export default DateTimeInput;
